import type { Currency } from "@/Apollo/schema";
import { LISTING_CURRENCY } from "@/common/static/currency.static";
import { US_LOCALE, locales } from "@/i18n/config";

/**
 * The display currency in the address (WHO-6199): `?currency=GBP` on a product or search page.
 *
 * Product and search pages are cached at the CDN per URL (WHO-5684), so a page rendered in pounds
 * needs a URL of its own, or one visitor's pounds would be served to everyone. The parameter is
 * that URL. The edge middleware (`middleware.ts`) sends a visitor to the variant their saved
 * choice asks for, and `getServerSideProps` renders the prices in it, so a buyer who picked £ sees
 * £ in the first paint instead of € for a moment.
 *
 * The SEO rules (Thomas, 20 Oct, in the ticket) this module carries out:
 *
 * - The visitor's own currency wins, never the link's: a `?currency=` that isn't theirs 302s to
 *   their own variant, or to the clean URL when they have none. Never a 301 (a currency is a
 *   preference, not a move), and only from a cookie, never by IP or language: Google must get the
 *   page users get, and a crawler has no cookie, so it always lands on the clean euro page.
 * - Their own currency is their pick (`display_currency`), else the first guess from their country
 *   (`display_currency_guess`, short-lived; Thomas, 22 Oct). IP only ever makes the guess, in
 *   `/api/display-currency-guess`; it never redirects. The very first landing, before the guess
 *   exists, still shows each listing's own currency first.
 * - Internal links stay clean: nothing the site renders carries the parameter (`patchQuery` drops
 *   it on the search page). Canonicals drop it (`Canonical`, `SearchSeo`), hreflang and sitemaps
 *   never have it, and JSON-LD stays in euros.
 *
 * Everything here is pure and edge-safe: the middleware imports it, so it must not reach the
 * generated schema module, React or anything Node-only.
 */

export const CURRENCY_URL_PARAM = "currency";

/** The buyer's pick, or the account's once they signed in. Never a guess. */
export const DISPLAY_CURRENCY_COOKIE = "display_currency";

/**
 * The first guess from the visitor's country, kept for a day so the edge can send them to their
 * variant from their second page view on (WHO-6199). Written only for a non-euro guess, and only
 * while the gate is on (`DisplayCurrencySync`); a euro guess is the clean URL and needs none.
 */
export const DISPLAY_CURRENCY_GUESS_COOKIE = "display_currency_guess";

/**
 * The currencies an address can carry: every display currency but the listings' own, which is
 * the clean URL. Literals with a type-only import, so the edge bundle doesn't load the generated
 * schema (as `LISTING_CURRENCY`); a test keeps them equal to `DISPLAY_CURRENCIES` minus EUR.
 */
export const URL_CURRENCIES = ["GBP", "USD"] as Currency[];

export const toUrlCurrency = (value: unknown): Currency | null =>
  URL_CURRENCIES.includes(value as Currency) ? (value as Currency) : null;

/**
 * The currency a visitor's address should carry, from their two cookies: a pick wins over the
 * guess, a picked EUR included (it means the clean URL). `null` for the clean URL.
 */
export const ownUrlCurrency = ({
  picked,
  guess,
}: {
  picked: string | undefined;
  guess: string | undefined;
}): Currency | null =>
  picked === LISTING_CURRENCY || toUrlCurrency(picked)
    ? toUrlCurrency(picked)
    : toUrlCurrency(guess);

/**
 * Locales whose path decides the currency (WHO-5766): an `/en-us/...` page renders USD for
 * everyone, from the server on, so it needs no `?currency=` and no redirect. The middleware leaves
 * those pages alone, `displayCurrencyForPage` answers with it, and `useDisplayCurrency` keeps it
 * after hydration whatever the visitor picked or the gate says. JSON-LD stays in euros (D3).
 * `/en-us` is routed only while `US_LAUNCH_ENABLED` is on, so with the flag off this entry is
 * never reached. A literal for the same edge-bundle reason as `URL_CURRENCIES`.
 */
export const LOCALE_CURRENCIES: Partial<Record<string, Currency>> = {
  [US_LOCALE]: "USD" as Currency,
};

export const localeCurrency = (locale: string | undefined) =>
  locale ? LOCALE_CURRENCIES[locale] : undefined;

/* Query pairs are read raw so the rest of the query string goes back byte for byte. */
const decode = (text: string) => {
  try {
    return decodeURIComponent(text.replace(/\+/g, " "));
  } catch {
    return text;
  }
};

const pairsOf = (search: string) =>
  search
    .replace(/^\?/, "")
    .split("&")
    .filter(Boolean)
    .map(raw => {
      const separator = raw.indexOf("=");
      const key = separator < 0 ? raw : raw.slice(0, separator);
      const value = separator < 0 ? "" : raw.slice(separator + 1);

      return { raw, key: decode(key), value: decode(value) };
    });

/**
 * The query string a request must have for a visitor whose own currency is `currency`
 * (`ownUrlCurrency`), or `null` when it already has it. The address names it exactly once (`GBP`,
 * upper case) or, for the clean URL, not at all. Every other parameter is kept as sent.
 */
export const currencyRedirectSearch = (
  search: string,
  currency: string | null | undefined
): string | null => {
  const own = toUrlCurrency(currency);
  const pairs = pairsOf(search);
  const inAddress = pairs.filter(pair => pair.key === CURRENCY_URL_PARAM);

  const matches = own
    ? inAddress.length === 1 && inAddress[0]?.value === own
    : inAddress.length === 0;

  if (matches) {
    return null;
  }

  const rest = pairs
    .filter(pair => pair.key !== CURRENCY_URL_PARAM)
    .map(pair => pair.raw);
  const next = own ? [...rest, `${CURRENCY_URL_PARAM}=${own}`] : rest;

  return next.length ? `?${next.join("&")}` : "";
};

/** The product page's first segment, per locale (`config/rewrites.js` serves the others). */
const PRODUCT_ROOTS = [
  "products",
  "producten",
  "produkte",
  "produits",
  "productos",
  "prodotti",
];

/**
 * Every route something other than the search catch-all (`pages/[...search].tsx`) answers, without
 * the locale: the `pages/` routes (bar the catch-all and the product page) and the sources of the
 * rewrites in `config/rewrites.js` that serve them. The catch-all takes every other path, so this
 * is how the edge tells a search or landing page from the rest without knowing the landing pages:
 * the search service owns those, and `/brands/<brand>` is one while `/brands` is not.
 *
 * `[x]` is one segment, `[...x]` one or more. `__tests__/features/display-currency/currency-url
 * .spec.ts` fails when a page or rewrite is added and this list doesn't have it.
 */
export const NON_SEARCH_ROUTES = [
  "/404",
  "/500",
  "/about",
  "/about/evelien",
  "/account/ads",
  "/account/ads/[id]",
  "/account/dashboard",
  "/account/favorites",
  "/account/login",
  "/account/notifications",
  "/account/pro-buyer-projects",
  "/account/pro-buyer-projects/[id]",
  "/account/promotions",
  "/account/promotions/[id]",
  "/account/purchases",
  "/account/purchases/[id]",
  "/account/refer-friend",
  "/account/reset-password",
  "/account/reset-password-confirm",
  "/account/reviews",
  "/account/sales",
  "/account/sales/[id]",
  "/account/searches",
  "/account/settings",
  "/account/settings/[path]",
  "/account/signup",
  "/account/vacation",
  "/agents",
  "/auth/magiclink",
  "/bidding",
  "/blog",
  "/blog/[slug]",
  "/blog/[slug]/preview",
  "/blog/page/[page]",
  "/brand/[...any]",
  "/brands",
  "/brands/all-brands",
  "/brenger/approver",
  "/brenger/approver/[orderId]",
  "/brenger/promo",
  "/brenger/scheduler",
  "/brenger/scheduler/[orderId]",
  "/business-to-business",
  "/cart2",
  "/cart2/overview",
  "/cart2/shipping",
  "/cart2/signup",
  "/chat",
  "/chat/[id]",
  "/checkout2",
  "/checkout2/complete",
  "/checkout2/packages/[id]",
  "/checkout2/services",
  "/collections",
  "/contact-us",
  "/create-ad/success",
  "/create-ad/v2",
  "/create-ad/wizard",
  "/curation",
  "/designers",
  "/designers/all-designers",
  "/edit-ad",
  "/edit-ad/[id]",
  "/eijerkamp",
  "/faq",
  "/feedback/[orderId]",
  "/glossary",
  "/help",
  "/help/[categorySlug]",
  "/help/[categorySlug]/[articleSlug]",
  "/help/search",
  "/how-it-works/buying",
  "/how-it-works/selling",
  "/icon",
  "/icon/[slug]",
  "/icon/[slug]/preview",
  "/impressum",
  "/invite",
  "/invite/[code]",
  "/leolux-landing",
  "/maintenance",
  "/oauth/consent",
  "/marcas",
  "/marcas/todas-las-marcas",
  "/marche",
  "/marche/tutte-le-marche",
  "/marken",
  "/marken/alle-marken",
  "/marques",
  "/marques/toutes-les-marques",
  "/merken",
  "/merken/alle-merken",
  "/open-home",
  "/open-home/favorites",
  "/open-home/items/[roomId]/[itemId]",
  "/open-home/my-home",
  "/open-home/offers",
  "/open-home/rooms/[id]",
  "/payment",
  "/photo-tips",
  "/privacy-policy",
  "/pro-buyer",
  "/pro-buyer/[word]",
  "/reviews",
  "/rss",
  "/saved-search/[id]",
  /*
   * WHO-6629: the Pro Buyer's client link; no listing prices in the address's currency.
   * `pages/selection` and its localized rewrites (WHO-6641, `config/selection-paths.js`).
   */
  "/selection/[token]",
  "/selectie/[token]",
  "/auswahl/[token]",
  "/seleccion/[token]",
  "/selezione/[token]",
  "/sellers-landing",
  "/shipping",
  "/similar",
  "/similar/[productSlug]",
  "/style",
  "/style/p/[page]",
  "/terms",
  "/us-waitinglist",
  "/user-ads/[merchantId]",
  /* `pages/value-check` and its localized rewrites (`config/rewrites.js`). */
  "/value-check",
  "/waardecheck",
  "/wertcheck",
  "/estimation",
  "/tasacion",
  "/valutazione",
];

const toRouteRegExp = (route: string) =>
  new RegExp(
    `^${route
      .split("/")
      .map(segment =>
        segment.startsWith("[...")
          ? "[^/]+(?:/[^/]+)*"
          : segment.startsWith("[")
          ? "[^/]+"
          : segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      )
      .join("/")}$`
  );

const NON_SEARCH_ROUTE_PATTERNS = NON_SEARCH_ROUTES.map(toRouteRegExp);

/**
 * Whether a path is a product page or a search or landing page, the pages whose prices the server
 * renders in the address's currency. Takes the path as requested, with or without the locale.
 */
export const isCurrencyVariantPath = (pathname: string): boolean => {
  const segments = pathname.split("/").filter(Boolean);

  if ((locales as readonly string[]).includes(segments[0] ?? "")) {
    segments.shift();
  }

  /* The home page: its prices come from the client only. */
  if (segments.length === 0) {
    return false;
  }

  if (segments.length === 2 && PRODUCT_ROOTS.includes(segments[0] ?? "")) {
    return true;
  }

  const path = `/${segments.join("/")}`;

  return !NON_SEARCH_ROUTE_PATTERNS.some(pattern => pattern.test(path));
};
